import React, { useEffect } from 'react';
import { Card, Space, Table, Tag, Input, Tooltip } from 'antd';
import moment from 'moment';
import { useCampaignOrderQueueContext } from '../../../../core/components/campaigns/campaignordersqueue/useCampaignOrderQueueContext';
import { Link } from 'react-router-dom';
import { formatCampaignName } from '../../../../core/utils/campaignNaming';
import { useCampaignRequestorContext } from '../../../../core/components/campaignRequestor/useCampaignRequestorContext';
import { createEnhancedSearchFilter } from '../../../../core/utils/enhancedSearch';

const { Search } = Input;

const columns = [
  {
    title: 'Campaign Details',
    dataIndex: 'name',
    key: 'name',
    sorter: true,
    render: (text, record) => (
      <Space size={4} direction="vertical">
        <Link to={`/campaigns/v2/orderqueue/details/${record.id}`}>
          {formatCampaignName(record)}
        </Link>
        <Space size={4}>
          {/* <Tag color="purple">ECM</Tag> */}
          <Tag color="blue">{(record.advertiser && record.advertiser.name) || ''}</Tag>
        </Space>
      </Space>
    ),
  },
  {
    title: 'Review Type',
    dataIndex: 'status',
    key: 'reviewType',
    render: (status) => {
      if (status === 'APPROVED') {
        return (
          <Tooltip title="Approved campaign order ready to launch.">
            <Tag color="green">Launch</Tag>
          </Tooltip>
        );
      }
      if (status === 'LIVE_APPROVED') {
        return (
          <Tooltip title="Approved updates ready to be applied to the live campaign.">
            <Tag color="gold">Update</Tag>
          </Tooltip>
        );
      }
      return <Tag>—</Tag>;
    },
  },
  {
    title: 'Status',
    dataIndex: 'status',
    key: 'status',
    render: (text, record) => {
      if (record.dspConfig && record.dspConfig.status) {
        switch (record.dspConfig.status) {
          case 'LAUNCHING':
            return <Tag color="orange">Launching</Tag>;
          case 'ERROR':
            return (
              <Tooltip title={record.dspConfig.error}>
                <Tag color="red">Error</Tag>
              </Tooltip>
            );

          default:
            break;
        }
      }
      switch (text) {
        case 'APPROVED':
          return 'Pending Launch';

        case 'LIVE_APPROVED':
          return 'Pending Update';

        default:
          break;
      }
    },
  },
  {
    title: 'Launch Date',
    dataIndex: 'startDate',
    key: 'startDate',
    sorter: true,
    render: (text) => moment(text).format('MM/DD/YYYY'),
  },
  {
    title: 'Submitted On',
    key: 'updatedAt',
    dataIndex: 'updatedAt',
    sorter: true,
    render: (text) => moment(text).format('MM/DD/YYYY'),
  },
];

export const CampaignOrderQueueReady = () => {
  const {
    campaignOrders,
    loadingCampaignOrdersQueue,
    fetchCampaignOrdersQueue,
    pagination,
    setPagination,
  } = useCampaignOrderQueueContext();
  const { currentUserOrg } = useCampaignRequestorContext();

  useEffect(() => {
    if (currentUserOrg) {
      const userData = localStorage.getItem('userData');
      const orgId = userData ? JSON.parse(userData).role.org.id : null;
      const currentOrgId = orgId ?? currentUserOrg.id;

      handleFetchCampaignOrdersQueue({
        skip: 0,
        first: pagination.pageSize,
        orgId: currentOrgId,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserOrg]);

  const handleFetchCampaignOrdersQueue = ({
    skip,
    first,
    name = '',
    sortField,
    sortOrder,
    orgId,
  }) => {
    const enhancedSearchFilter = createEnhancedSearchFilter(name);
    const where = {
      AND: [
        {
          status_in: ['APPROVED', 'LIVE_APPROVED'],
        },
        {
          orgs_some: {
            OR: [{ id: orgId }, { parentOrg_some: { id: orgId } }],
          },
        },
      ],
    };

    if (enhancedSearchFilter) {
      where['AND'].push(enhancedSearchFilter);
    }
    fetchCampaignOrdersQueue({
      variables: {
        where,
        orderBy:
          sortField && sortOrder ? `${sortField}_${sortOrder.toUpperCase()}` : 'updatedAt_DESC',
        skip,
        first,
      },
    });
  };

  const handleTableChange = ({ current, pageSize }, filters, sorter) => {
    const sortField = sorter.field;
    const sortOrder =
      sorter.order === 'ascend' ? 'asc' : sorter.order === 'descend' ? 'desc' : null;

    setPagination((prev) => ({ ...prev, current, pageSize }));
    handleFetchCampaignOrdersQueue({
      skip: (current - 1) * pageSize || 0,
      first: pageSize,
      sortField,
      sortOrder,
    });
  };

  const handleSearchChange = (name) => {
    handleFetchCampaignOrdersQueue({
      first: pagination.pageSize,
      name,
    });
  };

  return (
    <Card
      title="READY TO LAUNCH"
      extra={
        <div>
          <Search
            placeholder="Search campaign"
            style={{
              width: 200,
            }}
            onSearch={(value) => handleSearchChange(value)}
            allowClear
          />
        </div>
      }
    >
      <Table
        columns={columns}
        dataSource={campaignOrders}
        loading={loadingCampaignOrdersQueue}
        pagination={pagination}
        onChange={handleTableChange}
      />
    </Card>
  );
};
